import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { User, Building, Lock, Save, Loader2, Upload, X, Palette, Mail, Power } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { AdminNav } from './AdminDashboard';
import { useAuth } from '../../lib/auth';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import API from '../../lib/api';
import { getImageUrl } from '../../lib/utils-img';

const tabList = [
  { id: 'profile', label: 'My Profile', icon: User },
  { id: 'site', label: 'Site Info', icon: Building },
  { id: 'email', label: 'Email', icon: Mail },
  { id: 'theme', label: 'Theme Colors', icon: Palette },
  { id: 'maintenance', label: 'Maintenance', icon: Power },
  { id: 'editor', label: 'Editor Password', icon: Lock },
];

const inputCls = 'bg-[#232323] border-white/[0.10] rounded-lg h-10 text-sm text-white placeholder:text-white/25 focus:border-gold focus:ring-1 focus:ring-gold/20';
const labelCls = 'text-[10px] uppercase tracking-widest text-white/35 mb-1 block';

function ProfileTab() {
  const { user, updateUser } = useAuth();
  const [form, setForm] = useState({ name: '', email: '' });
  const [pwForm, setPwForm] = useState({ new_password: '', confirm: '' });
  const [saving, setSaving] = useState(false);
  const [savingPw, setSavingPw] = useState(false);

  useEffect(() => { if (user) setForm({ name: user.name || '', email: user.email || '' }); }, [user]);

  const saveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await API.put('/admin/profile', form);
      updateUser(data);
      toast.success('Profile updated');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update profile'); }
    setSaving(false);
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pwForm.new_password !== pwForm.confirm) { toast.error('Passwords do not match'); return; }
    if (pwForm.new_password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setSavingPw(true);
    try {
      await API.put('/admin/password', { new_password: pwForm.new_password });
      toast.success('Password changed');
      setPwForm({ new_password: '', confirm: '' });
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to change password'); }
    setSavingPw(false);
  };

  return (
    <div className="space-y-6">
      <form onSubmit={saveProfile} className="bg-[#1c1c1c] border border-white/[0.06] rounded-xl p-6" data-testid="profile-form">
        <h3 className="font-heading text-lg text-white font-semibold mb-4">Profile Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><label className={labelCls}>Name</label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="profile-name" /></div>
          <div><label className={labelCls}>Email</label><Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className={inputCls} data-testid="profile-email" /></div>
        </div>
        <Button type="submit" disabled={saving} className="mt-4 bg-gold text-white hover:bg-[#9A7B1F] rounded-lg text-xs uppercase tracking-widest gap-2" data-testid="save-profile-btn">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save Profile
        </Button>
      </form>

      <form onSubmit={changePassword} className="bg-[#1c1c1c] border border-white/[0.06] rounded-xl p-6" data-testid="password-form">
        <h3 className="font-heading text-lg text-white font-semibold mb-4">Set New Password</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><label className={labelCls}>New Password</label><Input type="password" value={pwForm.new_password} onChange={e => setPwForm({ ...pwForm, new_password: e.target.value })} required className={inputCls} data-testid="new-password" /></div>
          <div><label className={labelCls}>Confirm Password</label><Input type="password" value={pwForm.confirm} onChange={e => setPwForm({ ...pwForm, confirm: e.target.value })} required className={inputCls} data-testid="confirm-password" /></div>
        </div>
        <Button type="submit" disabled={savingPw} className="mt-4 bg-white/[0.08] text-white hover:bg-white/[0.12] border border-white/[0.08] rounded-lg text-xs uppercase tracking-widest gap-2" data-testid="change-password-btn">
          {savingPw ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Lock className="w-3.5 h-3.5" />} Change Password
        </Button>
      </form>
    </div>
  );
}

function SiteInfoTab() {
  const [form, setForm] = useState({ logo_url: '', logo_height: 48, address: '', phone: '', contact_email: '', footer_description: '', notification_email: '' });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    API.get('/admin/settings').then(r => {
      const site = r.data?.site || {};
      setForm({ logo_url: site.logo_url || '', logo_height: site.logo_height || 48, address: site.address || '', phone: site.phone || '', contact_email: site.contact_email || '', footer_description: site.footer_description || '', notification_email: site.notification_email || '' });
    }).catch(() => {});
  }, []);

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await API.post('/admin/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm(f => ({ ...f, logo_url: data.url }));
      toast.success('Logo uploaded');
    } catch { toast.error('Upload failed'); }
    setUploading(false);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await API.put('/admin/settings/site', form);
      toast.success('Site info saved — logo will update on next page load');
    } catch { toast.error('Failed to save'); }
    setSaving(false);
  };

  return (
    <form onSubmit={save} className="bg-[#1c1c1c] border border-white/[0.06] rounded-xl p-6" data-testid="site-info-form">
      <h3 className="font-heading text-lg text-white font-semibold mb-4">Site Information</h3>
      <div className="mb-5">
        <label className={labelCls}>Logo</label>
        <div className="flex items-center gap-4">
          {form.logo_url ? (
            <div className="relative group">
              <img src={getImageUrl(form.logo_url)} alt="Logo" style={{ height: `${form.logo_height || 48}px` }} className="w-auto object-contain rounded-lg border border-white/[0.08] p-2 bg-[#232323]" />
              <button type="button" onClick={() => setForm(f => ({ ...f, logo_url: '' }))} className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <X className="w-3 h-3" />
              </button>
            </div>
          ) : null}
          <label className="cursor-pointer flex items-center gap-2 px-4 py-2.5 border border-dashed border-white/[0.15] rounded-lg text-white/40 hover:border-gold hover:text-gold transition-colors text-xs">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {uploading ? 'Uploading...' : 'Upload Logo'}
            <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" data-testid="logo-upload-input" />
          </label>
        </div>
        {form.logo_url && (
          <div className="mt-4 flex items-end gap-4">
            <div>
              <label className={labelCls}>Logo Height (px)</label>
              <div className="flex items-center gap-3">
                <input type="range" min={24} max={120} value={form.logo_height || 48} onChange={e => setForm(f => ({ ...f, logo_height: parseInt(e.target.value) }))}
                  className="w-36 accent-[#B49128]" data-testid="logo-height-slider" />
                <Input type="number" min={24} max={120} value={form.logo_height || 48} onChange={e => setForm(f => ({ ...f, logo_height: parseInt(e.target.value) || 48 }))}
                  className={`${inputCls} w-20 text-center`} data-testid="logo-height-input" />
              </div>
            </div>
            <p className="text-[10px] text-white/25 pb-1">Width adjusts automatically</p>
          </div>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div><label className={labelCls}>Address</label><Input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} placeholder="123 Main St, Toronto, ON" className={inputCls} data-testid="site-address" /></div>
        <div><label className={labelCls}>Phone Number</label><Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="+1 (416) 555-0100" className={inputCls} data-testid="site-phone" /></div>
        <div><label className={labelCls}>Contact Email (shown on site)</label><Input type="email" value={form.contact_email} onChange={e => setForm({ ...form, contact_email: e.target.value })} placeholder="info@plushresidence.ca" className={inputCls} data-testid="site-contact-email" /></div>
        <div><label className={labelCls}>Notification Email (receive form messages)</label><Input type="email" value={form.notification_email} onChange={e => setForm({ ...form, notification_email: e.target.value })} placeholder="admin@plushresidence.ca" className={inputCls} data-testid="site-notification-email" /></div>
        <div className="md:col-span-2"><label className={labelCls}>Footer Description (text under logo)</label>
          <textarea value={form.footer_description} onChange={e => setForm({ ...form, footer_description: e.target.value })} rows={3} placeholder="Premium short-term rentals across Ontario..."
            className="w-full bg-[#232323] border border-white/[0.10] rounded-lg p-3 text-sm text-white resize-none focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold/20 placeholder:text-white/25" data-testid="site-footer-description" />
        </div>
      </div>
      <Button type="submit" disabled={saving} className="mt-4 bg-gold text-white hover:bg-[#9A7B1F] rounded-lg text-xs uppercase tracking-widest gap-2" data-testid="save-site-info-btn">
        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save Site Info
      </Button>
    </form>
  );
}


function EmailTab() {
  const [form, setForm] = useState({
    smtp_host: 'smtp.gmail.com',
    smtp_port: '587',
    smtp_user: '',
    smtp_pass: '',
    notification_email: '',
  });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    API.get('/admin/settings').then(r => {
      const smtp = r.data?.smtp || {};
      setForm(f => ({
        smtp_host: smtp.smtp_host || f.smtp_host,
        smtp_port: smtp.smtp_port || f.smtp_port,
        smtp_user: smtp.smtp_user || f.smtp_user,
        smtp_pass: smtp.smtp_pass || f.smtp_pass,
        notification_email: smtp.notification_email || r.data?.site?.notification_email || f.notification_email,
      }));
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    if (!form.smtp_user || !form.smtp_pass) { toast.error('Email and password are required'); return; }
    if (!form.notification_email) { toast.error('Notification email is required'); return; }
    setSaving(true);
    try {
      await API.put('/admin/settings/smtp', form);
      toast.success('Email settings saved');
    } catch { toast.error('Failed to save'); }
    setSaving(false);
  };

  const testEmail = async () => {
    if (!form.smtp_user || !form.smtp_pass || !form.notification_email) {
      toast.error('Please fill in all fields and save first');
      return;
    }
    setTesting(true);
    try {
      const { data } = await API.post('/admin/test-email');
      if (data.success) toast.success('Test email sent! Check your inbox (and spam folder).');
      else toast.error(data.error || 'Failed to send test email');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send test email');
    }
    setTesting(false);
  };

  if (!loaded) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-gold" /></div>;

  return (
    <form onSubmit={save} className="space-y-6" data-testid="email-settings-form">
      <div className="bg-[#1c1c1c] border border-white/[0.06] rounded-xl p-6">
        <h3 className="font-heading text-lg text-white font-semibold mb-1">Email Notifications</h3>
        <p className="text-sm text-white/30 mb-6">Configure Gmail SMTP to receive contact form submissions via email.</p>

        <div className="space-y-4">
          <div>
            <label className={labelCls}>Send notifications to *</label>
            <Input value={form.notification_email} onChange={e => setForm(f => ({ ...f, notification_email: e.target.value }))} type="email" placeholder="plushresidence@gmail.com" className={inputCls} data-testid="email-notification-to" />
            <p className="text-[10px] text-white/20 mt-1">Contact form submissions will be emailed here</p>
          </div>

          <div className="border-t border-white/[0.06] pt-4 mt-4">
            <p className="text-xs text-white/40 font-medium uppercase tracking-widest mb-3">Gmail SMTP Credentials</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Gmail Address *</label>
              <Input value={form.smtp_user} onChange={e => setForm(f => ({ ...f, smtp_user: e.target.value }))} type="email" placeholder="youremail@gmail.com" className={inputCls} data-testid="smtp-user" />
            </div>
            <div>
              <label className={labelCls}>App Password *</label>
              <Input value={form.smtp_pass} onChange={e => setForm(f => ({ ...f, smtp_pass: e.target.value }))} type="password" placeholder="xxxx xxxx xxxx xxxx" className={inputCls} data-testid="smtp-pass" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>SMTP Host</label>
              <Input value={form.smtp_host} onChange={e => setForm(f => ({ ...f, smtp_host: e.target.value }))} placeholder="smtp.gmail.com" className={inputCls} data-testid="smtp-host" />
            </div>
            <div>
              <label className={labelCls}>SMTP Port</label>
              <Input value={form.smtp_port} onChange={e => setForm(f => ({ ...f, smtp_port: e.target.value }))} placeholder="587" className={inputCls} data-testid="smtp-port" />
            </div>
          </div>
        </div>
      </div>

      <div className="bg-[#1c1c1c] border border-white/[0.06] rounded-xl p-6">
        <h4 className="font-heading text-sm text-white font-semibold mb-2">How to get a Gmail App Password</h4>
        <ol className="text-xs text-white/35 space-y-1.5 list-decimal list-inside">
          <li>Go to <a href="https://myaccount.google.com/security" target="_blank" rel="noopener noreferrer" className="text-gold underline">Google Account Security</a></li>
          <li>Enable <strong className="text-white/50">2-Step Verification</strong> if not already on</li>
          <li>Go to <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noopener noreferrer" className="text-gold underline">App Passwords</a></li>
          <li>Create a new app password (name it "PlushResidence")</li>
          <li>Copy the 16-character password and paste it above</li>
        </ol>
      </div>

      <div className="flex gap-3">
        <Button type="submit" disabled={saving} className="bg-gold text-white hover:bg-[#9A7B1F] rounded-lg text-xs uppercase tracking-widest gap-2" data-testid="save-email-btn">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save Settings
        </Button>
        <Button type="button" onClick={testEmail} disabled={testing} variant="outline" className="border-white/[0.10] text-white/50 hover:text-gold hover:border-gold rounded-lg text-xs uppercase tracking-widest gap-2" data-testid="test-email-btn">
          {testing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />} Send Test Email
        </Button>
      </div>
    </form>
  );
}


function NavigationTab() {
  const DEFAULT_HEADER = [
    { label: 'Home', path: '/', dropdown: false, visible: true },
    { label: 'Locations', path: '/locations', dropdown: true, visible: true },
    { label: 'About', path: '/about', dropdown: false, visible: true },
    { label: 'Contact', path: '/contact', dropdown: false, visible: true },
    { label: 'Policies', path: '/policies', dropdown: false, visible: true },
  ];
  const DEFAULT_FOOTER = [
    { label: 'Home', path: '/', visible: true },
    { label: 'Locations', path: '/locations', visible: true },
    { label: 'About', path: '/about', visible: true },
    { label: 'Contact', path: '/contact', visible: true },
    { label: 'Policies', path: '/policies', visible: true },
  ];

  const [headerItems, setHeaderItems] = useState(DEFAULT_HEADER);
  const [footerLinks, setFooterLinks] = useState(DEFAULT_FOOTER);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    API.get('/admin/settings').then(r => {
      const nav = r.data?.navigation || {};
      if (nav.header_items?.length) setHeaderItems(nav.header_items);
      if (nav.footer_links?.length) setFooterLinks(nav.footer_links);
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await API.put('/admin/settings/navigation', { header_items: headerItems, footer_links: footerLinks });
      toast.success('Navigation saved — changes will appear on next page load');
    } catch { toast.error('Failed to save navigation'); }
    setSaving(false);
  };

  const updateHeaderItem = (idx, field, value) => {
    setHeaderItems(items => items.map((item, i) => i === idx ? { ...item, [field]: value } : item));
  };

  const updateFooterLink = (idx, field, value) => {
    setFooterLinks(links => links.map((link, i) => i === idx ? { ...link, [field]: value } : link));
  };

  const addHeaderItem = () => {
    setHeaderItems(items => [...items, { label: 'New Link', path: '/', dropdown: false, visible: true }]);
  };

  const removeHeaderItem = (idx) => {
    setHeaderItems(items => items.filter((_, i) => i !== idx));
  };

  const addFooterLink = () => {
    setFooterLinks(links => [...links, { label: 'New Link', path: '/', visible: true }]);
  };

  const removeFooterLink = (idx) => {
    setFooterLinks(links => links.filter((_, i) => i !== idx));
  };

  const moveItem = (list, setList, idx, dir) => {
    const newList = [...list];
    const target = idx + dir;
    if (target < 0 || target >= newList.length) return;
    [newList[idx], newList[target]] = [newList[target], newList[idx]];
    setList(newList);
  };

  if (!loaded) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-gold" /></div>;

  return (
    <div className="space-y-6" data-testid="navigation-tab">
      <div className="bg-[#1c1c1c] border border-white/[0.06] rounded-xl p-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className="font-heading text-lg text-white font-semibold">Header Navigation</h3>
            <p className="text-sm text-white/30 mt-1">Manage the links that appear in the top navigation bar.</p>
          </div>
          <button type="button" onClick={addHeaderItem} className="text-xs text-gold hover:text-gold/80 transition-colors uppercase tracking-widest font-medium" data-testid="add-header-item">+ Add Item</button>
        </div>
        <div className="space-y-3">
          {headerItems.map((item, idx) => (
            <div key={idx} className="flex items-center gap-3 p-3 bg-[#232323] rounded-lg border border-white/[0.06]" data-testid={`header-item-${idx}`}>
              <div className="flex flex-col gap-0.5">
                <button type="button" onClick={() => moveItem(headerItems, setHeaderItems, idx, -1)} disabled={idx === 0} className="text-white/20 hover:text-white disabled:opacity-20 text-xs">&#9650;</button>
                <button type="button" onClick={() => moveItem(headerItems, setHeaderItems, idx, 1)} disabled={idx === headerItems.length - 1} className="text-white/20 hover:text-white disabled:opacity-20 text-xs">&#9660;</button>
              </div>
              <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-2">
                <Input value={item.label} onChange={e => updateHeaderItem(idx, 'label', e.target.value)} placeholder="Label" className={inputCls} data-testid={`header-item-label-${idx}`} />
                <Input value={item.path} onChange={e => updateHeaderItem(idx, 'path', e.target.value)} placeholder="/path" className={inputCls} data-testid={`header-item-path-${idx}`} />
              </div>
              <label className="flex items-center gap-1.5 text-xs text-white/40 cursor-pointer shrink-0">
                <input type="checkbox" checked={item.dropdown || false} onChange={e => updateHeaderItem(idx, 'dropdown', e.target.checked)} className="accent-[#B49128]" />
                Dropdown
              </label>
              <label className="flex items-center gap-1.5 text-xs text-white/40 cursor-pointer shrink-0">
                <input type="checkbox" checked={item.visible !== false} onChange={e => updateHeaderItem(idx, 'visible', e.target.checked)} className="accent-[#B49128]" />
                Visible
              </label>
              <button type="button" onClick={() => removeHeaderItem(idx)} className="p-1.5 text-white/20 hover:text-red-400 transition-colors shrink-0" data-testid={`remove-header-item-${idx}`}>
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-[#1c1c1c] border border-white/[0.06] rounded-xl p-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className="font-heading text-lg text-white font-semibold">Footer Quick Links</h3>
            <p className="text-sm text-white/30 mt-1">Manage the links that appear in the footer "Quick Links" section.</p>
          </div>
          <button type="button" onClick={addFooterLink} className="text-xs text-gold hover:text-gold/80 transition-colors uppercase tracking-widest font-medium" data-testid="add-footer-link">+ Add Link</button>
        </div>
        <div className="space-y-3">
          {footerLinks.map((link, idx) => (
            <div key={idx} className="flex items-center gap-3 p-3 bg-[#232323] rounded-lg border border-white/[0.06]" data-testid={`footer-link-${idx}`}>
              <div className="flex flex-col gap-0.5">
                <button type="button" onClick={() => moveItem(footerLinks, setFooterLinks, idx, -1)} disabled={idx === 0} className="text-white/20 hover:text-white disabled:opacity-20 text-xs">&#9650;</button>
                <button type="button" onClick={() => moveItem(footerLinks, setFooterLinks, idx, 1)} disabled={idx === footerLinks.length - 1} className="text-white/20 hover:text-white disabled:opacity-20 text-xs">&#9660;</button>
              </div>
              <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-2">
                <Input value={link.label} onChange={e => updateFooterLink(idx, 'label', e.target.value)} placeholder="Label" className={inputCls} data-testid={`footer-link-label-${idx}`} />
                <Input value={link.path} onChange={e => updateFooterLink(idx, 'path', e.target.value)} placeholder="/path" className={inputCls} data-testid={`footer-link-path-${idx}`} />
              </div>
              <label className="flex items-center gap-1.5 text-xs text-white/40 cursor-pointer shrink-0">
                <input type="checkbox" checked={link.visible !== false} onChange={e => updateFooterLink(idx, 'visible', e.target.checked)} className="accent-[#B49128]" />
                Visible
              </label>
              <button type="button" onClick={() => removeFooterLink(idx)} className="p-1.5 text-white/20 hover:text-red-400 transition-colors shrink-0" data-testid={`remove-footer-link-${idx}`}>
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </div>

      <Button type="button" onClick={save} disabled={saving} className="bg-gold hover:bg-[#9A7B1F] text-white rounded-lg text-xs uppercase tracking-widest gap-2" data-testid="save-navigation-btn">
        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save Navigation
      </Button>
    </div>
  );
}


function MaintenanceTab() {
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    API.get('/site-status').then(r => {
      setEnabled(r.data.maintenance_mode === true);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const toggle = async () => {
    const newVal = !enabled;
    setSaving(true);
    try {
      await API.put('/admin/maintenance', { enabled: newVal });
      setEnabled(newVal);
      toast.success(newVal ? 'Website is now in maintenance mode' : 'Website is back online');
    } catch { toast.error('Failed to update maintenance mode'); }
    setSaving(false);
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-gold" /></div>;

  return (
    <div className="space-y-6" data-testid="maintenance-tab">
      <div className={`relative bg-[#1c1c1c] border rounded-xl p-6 transition-colors ${enabled ? 'border-red-500/30' : 'border-white/[0.06]'}`}>
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-heading text-lg text-white font-semibold mb-1">Maintenance Mode</h3>
            <p className="text-sm text-white/30">When enabled, all visitors will see a "We'll Be Back Soon" page. Admin panel and login remain accessible.</p>
          </div>
          <button
            type="button" onClick={toggle} disabled={saving}
            className={`relative w-14 h-7 rounded-full transition-colors shrink-0 ml-6 ${enabled ? 'bg-red-500' : 'bg-white/10'}`}
            data-testid="maintenance-toggle"
          >
            {saving && <Loader2 className="w-3 h-3 animate-spin text-white absolute top-2 left-1/2 -translate-x-1/2" />}
            <div className={`absolute top-1 w-5 h-5 rounded-full bg-white shadow transition-transform ${enabled ? 'translate-x-8' : 'translate-x-1'}`} />
          </button>
        </div>
        {enabled && (
          <div className="mt-4 flex items-center gap-2 px-3 py-2 bg-red-500/10 border border-red-500/20 rounded-lg">
            <span className="w-2 h-2 rounded-full bg-red-400 animate-pulse" />
            <span className="text-xs text-red-300 font-medium">Website is currently offline for visitors</span>
          </div>
        )}
      </div>
    </div>
  );
}

function ThemeColorsTab() {
  const COLOR_FIELDS = [
    { key: 'color_heading', label: 'Headings (H1, H2, H3)', desc: 'Controls the color of all headings across the site', defaultVal: '#ffffff' },
    { key: 'color_body', label: 'Body Text', desc: 'Main paragraph and body text color', defaultVal: 'rgba(255,255,255,0.55)' },
    { key: 'color_muted', label: 'Muted / Secondary Text', desc: 'Subtitles, captions, and less prominent text', defaultVal: 'rgba(255,255,255,0.35)' },
    { key: 'color_accent', label: 'Accent Color', desc: 'Gold accents, buttons, and highlights. Also updates the site\'s accent/gold color', defaultVal: '#B49128' },
    { key: 'color_link', label: 'Link Color', desc: 'Color of clickable links', defaultVal: '#B49128' },
  ];

  const [form, setForm] = useState({
    color_heading: '#ffffff',
    color_body: '#8c8c8c',
    color_muted: '#595959',
    color_accent: '#B49128',
    color_link: '#B49128',
  });
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    API.get('/admin/settings').then(r => {
      const colors = r.data?.theme_colors || {};
      setForm(f => ({
        color_heading: colors.color_heading || f.color_heading,
        color_body: colors.color_body || f.color_body,
        color_muted: colors.color_muted || f.color_muted,
        color_accent: colors.color_accent || f.color_accent,
        color_link: colors.color_link || f.color_link,
      }));
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, []);

  const rgbaToHex = (rgba) => {
    if (!rgba || rgba.startsWith('#')) return rgba;
    const match = rgba.match(/[\d.]+/g);
    if (!match) return '#ffffff';
    const [r, g, b] = match.map(Number);
    return `#${[r, g, b].map(c => Math.round(c).toString(16).padStart(2, '0')).join('')}`;
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await API.put('/admin/settings/theme_colors', form);
      toast.success('Theme colors saved — changes will appear on next page load');
    } catch { toast.error('Failed to save theme colors'); }
    setSaving(false);
  };

  const resetDefaults = () => {
    setForm({
      color_heading: '#ffffff',
      color_body: '#8c8c8c',
      color_muted: '#595959',
      color_accent: '#B49128',
      color_link: '#B49128',
    });
  };

  if (!loaded) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-gold" /></div>;

  return (
    <form onSubmit={save} className="space-y-6" data-testid="theme-colors-form">
      <div className="bg-[#1c1c1c] border border-white/[0.06] rounded-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="font-heading text-lg text-white font-semibold">Theme Colors</h3>
            <p className="text-sm text-white/40 mt-1">Customize the text colors displayed across the public-facing website.</p>
          </div>
          <button type="button" onClick={resetDefaults} className="text-xs text-white/30 hover:text-gold transition-colors uppercase tracking-widest" data-testid="reset-theme-colors">Reset Defaults</button>
        </div>

        <div className="space-y-5">
          {COLOR_FIELDS.map(field => (
            <div key={field.key} className="flex items-start gap-4 p-4 bg-[#232323] rounded-lg border border-white/[0.06]">
              <div className="shrink-0 mt-1">
                <label className="relative cursor-pointer block">
                  <input
                    type="color"
                    value={rgbaToHex(form[field.key])}
                    onChange={e => setForm(f => ({ ...f, [field.key]: e.target.value }))}
                    className="absolute inset-0 opacity-0 cursor-pointer w-10 h-10"
                    data-testid={`color-picker-${field.key}`}
                  />
                  <div className="w-10 h-10 rounded-lg border-2 border-white/[0.12] shadow-inner" style={{ backgroundColor: form[field.key] }} />
                </label>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-1">
                  <span className="text-sm font-medium text-white">{field.label}</span>
                </div>
                <p className="text-xs text-white/30 mb-2">{field.desc}</p>
                <Input
                  value={form[field.key]}
                  onChange={e => setForm(f => ({ ...f, [field.key]: e.target.value }))}
                  placeholder={field.defaultVal}
                  className={`${inputCls} max-w-[200px] text-xs`}
                  data-testid={`color-input-${field.key}`}
                />
              </div>
              <div className="shrink-0 mt-1">
                <div className="text-right">
                  <span className="block text-xs text-white/20 mb-1">Preview</span>
                  <span className="text-sm font-medium" style={{ color: form[field.key] }}>Sample Text</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-[#1c1c1c] border border-white/[0.06] rounded-xl p-6">
        <h4 className="font-heading text-sm text-white font-semibold mb-3">Live Preview</h4>
        <div className="bg-[#101010] rounded-lg p-6 border border-white/[0.06]">
          <h1 className="font-heading text-2xl mb-2" style={{ color: form.color_heading }}>Heading Example</h1>
          <p className="text-sm mb-2" style={{ color: form.color_body }}>This is how body text will look across the site. It should be easy to read against the dark background.</p>
          <p className="text-xs mb-2" style={{ color: form.color_muted }}>This is muted text for subtitles, captions, and less prominent information.</p>
          <a href="#" onClick={e => e.preventDefault()} className="text-sm underline" style={{ color: form.color_link }}>This is a link example</a>
          <span className="mx-2 text-white/10">|</span>
          <span className="text-sm font-semibold" style={{ color: form.color_accent }}>Accent Text</span>
        </div>
      </div>

      <Button type="submit" disabled={saving} className="bg-gold text-white hover:bg-[#9A7B1F] rounded-lg text-xs uppercase tracking-widest gap-2" data-testid="save-theme-colors-btn">
        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save Theme Colors
      </Button>
    </form>
  );
}

function EditorPasswordTab() {
  const [form, setForm] = useState({ password: '' });
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    if (form.password.length < 4) { toast.error('Password must be at least 4 characters'); return; }
    setSaving(true);
    try {
      await API.put('/admin/editor-password', { password: form.password });
      toast.success('Editor password updated');
      setForm({ password: '' });
    } catch { toast.error('Failed to update'); }
    setSaving(false);
  };

  return (
    <form onSubmit={save} className="bg-[#1c1c1c] border border-white/[0.06] rounded-xl p-6" data-testid="editor-password-form">
      <h3 className="font-heading text-lg text-white font-semibold mb-2">Visual Editor Password</h3>
      <p className="text-sm text-white/40 mb-4">Set a password required to access the visual page editor. Default: <code className="bg-white/[0.06] px-1.5 py-0.5 rounded text-xs text-gold">editor123</code></p>
      <div className="max-w-sm">
        <label className={labelCls}>New Editor Password</label>
        <Input type="password" value={form.password} onChange={e => setForm({ password: e.target.value })} required placeholder="Enter new editor password" className={inputCls} data-testid="editor-password-input" />
      </div>
      <Button type="submit" disabled={saving} className="mt-4 bg-white/[0.08] text-white hover:bg-white/[0.12] border border-white/[0.08] rounded-lg text-xs uppercase tracking-widest gap-2" data-testid="save-editor-password-btn">
        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Lock className="w-3.5 h-3.5" />} Update Editor Password
      </Button>
    </form>
  );
}

export default function AdminSettings() {
  const { isAdmin, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('profile');

  useEffect(() => { if (!authLoading && !isAdmin) navigate('/login'); }, [isAdmin, authLoading, navigate]);

  if (authLoading) return <div className="min-h-screen pt-20 flex items-center justify-center"><div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="min-h-screen pt-20 bg-[#101010]" data-testid="admin-settings">
      <div className="bg-[#161616] border-b border-white/[0.06] py-8 px-4 sm:px-8">
        <div className="max-w-7xl mx-auto">
          <p className="text-gold text-xs uppercase tracking-[0.3em] font-semibold">Admin</p>
          <h1 className="font-heading text-2xl text-white font-bold mt-1">Settings</h1>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8">
        <AdminNav active="/admin/settings" />
        <div className="flex flex-col md:flex-row gap-6">
          <aside className="md:w-56 shrink-0">
            <nav className="flex md:flex-col gap-1" data-testid="settings-tabs">
              {tabList.map(t => (
                <button key={t.id} onClick={() => setTab(t.id)}
                  className={`flex items-center gap-2.5 px-4 py-2.5 text-sm font-medium rounded-lg transition-colors text-left w-full
                    ${tab === t.id ? 'bg-gold/15 text-gold' : 'text-white/40 hover:text-white hover:bg-white/[0.05]'}`}
                  data-testid={`settings-tab-${t.id}`}
                >
                  <t.icon className="w-4 h-4" /> {t.label}
                </button>
              ))}
            </nav>
          </aside>
          <div className="flex-1 min-w-0">
            <motion.div key={tab} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }}>
              {tab === 'profile' && <ProfileTab />}
              {tab === 'site' && <SiteInfoTab />}
              {tab === 'email' && <EmailTab />}
              {tab === 'theme' && <ThemeColorsTab />}
              {tab === 'maintenance' && <MaintenanceTab />}
              {tab === 'editor' && <EditorPasswordTab />}
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}
